import { Box, Button, Card, CardContent, CardHeader, IconButton, Input, InputAdornment, Tooltip, } from "@mui/material";
import React, { useEffect, useState, useCallback } from "react";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DataTable from "react-data-table-component";
import { CustomerSupport } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import TableFilter from "../../components/shared/TableFilter";
import { SUPPORT_OPTIONS_LIST } from "../../utils/constants";
import { useNavigate } from "react-router-dom";
import { Loader } from "../../components/CustomLoader" ;
import CustomPagination from "../../components/CustomPagination";
import { debounce } from "lodash";
import { fDate } from "../../utils/formatTime";
import { capitalizeFirstLetter, TrimText } from "../../utils/helpers";

export default function CustomerSupportList() {
  const navigate = useNavigate();
  const [searchItem, setSearchItem] = useState("");
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [refresh, setRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);
  const [queryStatus, setQueryStatus] = useState([]);

  const fetchQuery = async (query) => {
    const body = {
      searchItem: query || "",
      status: queryStatus,
      pageNumber: currentPage,
      pageSize: pageSize,
    };
    try {
      setIsLoading(true);
      setLoaderDelay(false);
      const res = await CustomerSupport.getAll(body);
      setTimeout(() => {
        if (res.status === 200) {
          setData(res?.data?.data?.requests);
          setTotalPages(res?.data?.data?.totalItems);
        }
        setLoaderDelay(true);
      }, 500);
    } catch (err) {
      setData(err?.response?.data?.message?.data);
      errorHandler(err);
      setLoaderDelay(false);
    } finally {
      setIsLoading(false);
      setLoaderDelay(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((query) => fetchQuery(query), 400),
    [currentPage, pageSize, queryStatus]
  );

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchItem(query);
    debouncedSearch(query);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  useEffect(() => {
    fetchQuery(searchItem);
  }, [currentPage, pageSize, refresh, queryStatus]);

  const columns = [
    {
      name: "S.No.",
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      name: "Name",
      sortable: true,
      selector: (row) => capitalizeFirstLetter(TrimText(row.name, 25)),
    },
    {
      name: "Email",
      sortable: true,
      selector: (row) => row.email,
    },
    {
      name: "Subject",
      sortable: true,
      selector: (row) => row.subject,
    },
    {
      name: "Query Status",
      sortable: true,
      selector: (row) => capitalizeFirstLetter(row.queryStatus),
    },
    {
      name: "Created At",
      sortable: true,
      selector: (row) => fDate(row.createdAt),
    },

    {
      name: "Action",
      style: {
        justifyContent: 'center',
      },
      cell: (row) => (
        <Tooltip title="View & Reply" placement="top">
          <IconButton
            color="primary"
            onClick={() => {
              navigate(`/customer-support/view/${row._id || row.id}`);
            }}
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Card>
      <title> Ziskat</title>
      <CardHeader title="Customer Support Management" />
      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="flex-end"
        alignItems="center"
        p={2}
        gap={2}
      >
        {/* Query Status Filter */}
        <TableFilter
          data={SUPPORT_OPTIONS_LIST}
          value={queryStatus}
          setValue={setQueryStatus}
          filterName="Query Status"
        />

        {/* Search Input */}
        <Input
          value={searchItem}
          placeholder="Search"
          onChange={handleSearchChange}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="clear-search"
                onClick={() => {
                  const cleared = "";
                  setSearchItem(cleared);
                  debouncedSearch(cleared);
                }}
              >
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          }
        />

        {/* Clear Button */}
        {(queryStatus.length > 0 || searchItem) && (
          <Button
            variant="outlined"
            onClick={() => {
              setQueryStatus([]);
              setSearchItem("");
            }}
            sx={{
              whiteSpace: "nowrap",
              minWidth: "100px",
            }}
          >
            Clear
          </Button>
        )}
      </Box>

      <CardContent>
        <DataTable
          customStyles={{
            headCells: {
              style: {
                fontSize: "15px",
                fontWeight: "bold",
              },
            },
          }}
          columns={columns}
          data={data}
          pagination
          paginationServer
          paginationTotalRows={totalPages}
          onChangePage={handlePageChange}
          paginationPerPage={pageSize}
          paginationRowsPerPageOptions={[5, 10, 20, 50]}
          onChangeRowsPerPage={(currentRowsPerPage) =>
            setPageSize(currentRowsPerPage)
          }
          progressPending={isLoading || !loaderDelay}
          progressComponent={<Loader />}
          paginationComponent={(props) => (
            <CustomPagination {...props} currentPage={currentPage} />
          )}
          className="custom"
        />
      </CardContent>
    </Card>
  );
}
